// ConnectionAuthDrawer — single right-side drawer mounted at app-shell
// level. Three invocation surfaces share this one component:
//
//   1. /dashboard/settings/connections page — page sets store.open=true
//      on mount so the drawer renders as a full-screen-feeling panel.
//   2. Den hero "Connect GitHub" CTA (DevVelocityWidget) — calls
//      openConnectionDrawer({ platformKey: "github", context: "den" }).
//   3. Suggested-action card "Connect N more essential accounts" —
//      openConnectionDrawer({ context: "suggested-action" }).
//
// The drawer reads the user's profile.self_identity and pipes it as the
// archetype to getCopyForUser for every row. Pending archetype slots
// transparently fall back to defaultCopy.
//
// Backdrop click intentionally does NOT close (per spec / CLAUDE.md rule).
// Escape key closes (built-in radix behavior).
// X button at top-right closes.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  PLATFORM_CONFIGS,
  ESSENTIAL_PLATFORM_KEYS,
  GROUP_PLATFORM_KEYS,
  MORE_PLATFORM_KEYS,
  getCopyForUser,
  type Archetype,
} from "@/lib/connectionConfigs";
import { ConnectionRow, type RowState, type UserConnection } from "./ConnectionRow";
import { closeConnectionDrawer, useConnectionDrawer } from "./connectionDrawerStore";

const SUPABASE_FN_URL = "https://wnzxffwiiubqvcvnnjbz.supabase.co/functions/v1/oauth-callback";

export function ConnectionAuthDrawer() {
  const { open, defaultExpanded } = useConnectionDrawer();
  const [archetype, setArchetype] = useState<Archetype | null>(null);
  const [connections, setConnections] = useState<UserConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingPlatform, setPendingPlatform] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [justConnected, setJustConnected] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // ── Load profile + connections ────────────────────────────────────
  const refresh = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }
    const [profRes, connRes] = await Promise.all([
      supabase.from("profiles").select("self_identity").eq("id", user.id).maybeSingle(),
      supabase.from("user_connections")
        .select("id, user_id, platform, account_handle, account_id, is_primary, scopes, connected_at, last_synced_at, disconnected_at")
        .eq("user_id", user.id)
        .is("disconnected_at", null)
        .not("account_handle", "like", "pending:%")
        .order("connected_at", { ascending: true }),
    ]);
    const ident = String((profRes.data as any)?.self_identity || "").toLowerCase() as Archetype;
    setArchetype(ident || null);
    setConnections((connRes.data as UserConnection[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => { if (open) { setLoading(true); refresh(); } }, [open, refresh]);

  // ── Detect post-OAuth return query params ─────────────────────────
  useEffect(() => {
    if (!open) return;
    const url = new URL(window.location.href);
    const status = url.searchParams.get("status");
    const platform = url.searchParams.get("platform");
    const errCode = url.searchParams.get("error");
    if (status === "connected" && platform) {
      const cfg = PLATFORM_CONFIGS[platform];
      url.searchParams.delete("status"); url.searchParams.delete("platform"); url.searchParams.delete("error");
      window.history.replaceState({}, "", url.toString());
      // Refresh connections, then toast with the actual handle and flash the row.
      (async () => {
        await refresh();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: row } = await supabase
            .from("user_connections")
            .select("account_handle")
            .eq("user_id", user.id)
            .eq("platform", platform)
            .is("disconnected_at", null)
            .order("connected_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          const handle = (row as any)?.account_handle as string | null;
          toast.success(handle ? `${cfg?.name || platform} connected as ${handle}` : `${cfg?.name || platform} connected`);
        } else {
          toast.success(`${cfg?.name || platform} connected`);
        }
        setJustConnected(platform);
        setTimeout(() => setJustConnected((p) => (p === platform ? null : p)), 3000);
      })();
    } else if (status === "error" && platform) {
      const cfg = PLATFORM_CONFIGS[platform];
      const msg = errorMessage(errCode);
      setErrors((e) => ({ ...e, [platform]: msg }));
      toast.error(`${cfg?.name || platform}: ${msg}`);
      url.searchParams.delete("status"); url.searchParams.delete("platform"); url.searchParams.delete("error");
      window.history.replaceState({}, "", url.toString());
    }
  }, [open, refresh]);

  // ── Scroll to defaultExpanded row on open ─────────────────────────
  useEffect(() => {
    if (!open || !defaultExpanded || loading) return;
    const t = setTimeout(() => {
      const el = contentRef.current?.querySelector(`[data-platform="${defaultExpanded}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t);
  }, [open, defaultExpanded, loading]);

  // ── Per-platform connection state derivation ──────────────────────
  const connsByPlatform = useMemo(() => {
    const out: Record<string, UserConnection[]> = {};
    for (const c of connections) {
      (out[c.platform] = out[c.platform] || []).push(c);
    }
    return out;
  }, [connections]);

  // Count only currently-enabled essentials toward the progress bar.
  // When a Tier 1 platform is in pendingReview state (e.g. LinkedIn awaiting
  // app approval) we surface the row but exclude it from the denominator
  // so the bar reads cleanly. The moment it flips to enabled=true the
  // count auto-adjusts back up.
  const enabledEssentials = ESSENTIAL_PLATFORM_KEYS.filter((k) => PLATFORM_CONFIGS[k].enabled);
  const essentialsConnected = enabledEssentials.filter((k) => (connsByPlatform[k]?.length || 0) > 0).length;
  const essentialsTotal = enabledEssentials.length;

  // ── Action handlers ──────────────────────────────────────────────
  async function callFn(body: any) {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch(SUPABASE_FN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token || ""}`,
      },
      body: JSON.stringify(body),
    });
    return r.json().catch(() => ({ ok: false, error: "bad_response" }));
  }

  const handleConnect = useCallback(async (platformKey: string) => {
    setPendingPlatform(platformKey);
    setErrors((e) => ({ ...e, [platformKey]: null }));
    const j = await callFn({
      action: "initiate",
      platform: platformKey,
      redirect_back_to: window.location.origin + window.location.pathname,
    });
    if (!j?.ok || !j?.url) {
      const msg = errorMessage(j?.error);
      setErrors((e) => ({ ...e, [platformKey]: msg }));
      toast.error(`${PLATFORM_CONFIGS[platformKey]?.name || platformKey}: ${msg}`);
      setPendingPlatform(null);
      return;
    }
    window.location.href = j.url;
  }, []);

  const handleDisconnect = useCallback(async (connectionId: string) => {
    const j = await callFn({ action: "disconnect", connection_id: connectionId });
    if (!j?.ok) {
      toast.error("Couldn't disconnect — please try again.");
      return;
    }
    toast.success("Disconnected");
    refresh();
  }, [refresh]);

  const handleSetPrimary = useCallback(async (connectionId: string) => {
    const j = await callFn({ action: "set-primary", connection_id: connectionId });
    if (!j?.ok) {
      toast.error("Couldn't set primary — please try again.");
      return;
    }
    refresh();
  }, [refresh]);

  const handleConnectMedium = useCallback(async (token: string) => {
    setPendingPlatform("medium");
    setErrors((e) => ({ ...e, medium: null }));
    const j = await callFn({ action: "connect-medium", token });
    setPendingPlatform(null);
    if (!j?.ok) {
      const msg = errorMessage(j?.error) + (j?.detail ? ` (${j.detail})` : "");
      setErrors((e) => ({ ...e, medium: msg }));
      toast.error(`Medium: ${msg}`);
      return;
    }
    toast.success(`Medium connected${j.account_handle ? ` as ${j.account_handle}` : ""}`);
    refresh();
  }, [refresh]);

  function rowState(platformKey: string): RowState {
    const cfg = PLATFORM_CONFIGS[platformKey];
    if (!cfg.enabled) return "disabled";
    if (errors[platformKey]) return "error";
    if (pendingPlatform === platformKey) return "connecting";
    if ((connsByPlatform[platformKey]?.length || 0) > 0) return "connected";
    return "disconnected";
  }

  function renderRow(platformKey: string) {
    const cfg = PLATFORM_CONFIGS[platformKey];
    const copy = getCopyForUser(platformKey, archetype);
    return (
      <ConnectionRow
        key={platformKey}
        platformKey={platformKey}
        config={cfg}
        copy={copy}
        state={rowState(platformKey)}
        connections={connsByPlatform[platformKey] || []}
        errorMessage={errors[platformKey]}
        highlight={defaultExpanded === platformKey || justConnected === platformKey}
        onConnect={() => handleConnect(platformKey)}
        onConnectAnother={() => handleConnect(platformKey)}
        onDisconnect={handleDisconnect}
        onSetPrimary={handleSetPrimary}
        onConnectMedium={platformKey === "medium" ? handleConnectMedium : undefined}
        onRetry={() => { setErrors((e) => ({ ...e, [platformKey]: null })); handleConnect(platformKey); }}
      />
    );
  }

  return (
    <TooltipProvider>
      <Sheet open={open} onOpenChange={(o) => { if (!o) closeConnectionDrawer(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[480px] sm:w-[480px] p-0 flex flex-col gap-0"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {/* Header */}
          <div ref={contentRef} className="overflow-y-auto h-full">
            <div className="sticky top-0 z-10 bg-background border-b border-border/60 px-5 py-4">
              <h2 className="text-lg font-bold text-foreground">Connections</h2>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Read-only links to the platforms where your work is visible. Powers the Den, the cohort comparison, and the dossier bio audit.
              </p>
              <p className="text-[11px] text-muted-foreground/90 mt-1.5">
                Connect your accounts to unlock real metrics. More platforms launching weekly.
              </p>
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">Essentials connected</span>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs max-w-[280px]">
                      The Tier 1 platforms power the most signals. Connect every enabled one to unlock the full Den view. Platforms awaiting OAuth approval show a Review pending badge and don't count toward this total until they're live.
                    </TooltipContent>
                  </Tooltip>
                  <span className="text-foreground tabular-nums">{essentialsConnected} of {essentialsTotal}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${essentialsTotal > 0 ? (essentialsConnected / essentialsTotal) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-5">
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading your connections…
                </div>
              ) : (
                <>
                  {/* ESSENTIALS */}
                  <section>
                    <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Essentials</h3>
                    <div className="space-y-2">
                      {ESSENTIAL_PLATFORM_KEYS.map(renderRow)}
                    </div>
                  </section>

                  {/* GROUPS */}
                  <section>
                    <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Groups</h3>
                    <div className="space-y-2">
                      {GROUP_PLATFORM_KEYS.map(renderRow)}
                    </div>
                  </section>

                  {/* MORE accordion */}
                  <section>
                    <Accordion type="single" collapsible>
                      <AccordionItem value="more" className="border-none">
                        <AccordionTrigger className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold hover:no-underline py-2">
                          More
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-2 pt-2">
                            {MORE_PLATFORM_KEYS.map(renderRow)}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </section>
                </>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function errorMessage(code: string | null | undefined): string {
  switch (code) {
    case "oauth_app_not_registered":
      return "OAuth app not yet registered. Saul will set this up before launch.";
    case "token_exchange_failed":
      return "The platform refused our token request. Try again, or reconnect from scratch.";
    case "medium_validation_failed":
      return "Medium rejected that token. Make sure it's the integration token from medium.com/me/settings.";
    case "no_pending_row":
      return "Connection state expired. Start the connect flow again.";
    case "store_failed":
      return "Couldn't save the connection — please try again.";
    case "exception":
      return "Something went wrong on our side. Please try again.";
    default:
      return code ? `Connection failed (${code}).` : "Connection failed.";
  }
}
